import { get } from '@/renderer/utils/get';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { useLocale } from '@/renderer/utils/locale';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { Field, Label, Radio, RadioGroup } from '@headlessui/react';
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ChevronRightIcon,
} from '@heroicons/react/16/solid';
import { CheckCircleIcon, InformationCircleIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { CheckIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { deepEqual } from 'fast-equals';
import { Fragment, useMemo, useState } from 'react';
import { RASPlusIcon } from './ProductIcons';

import Skeleton from '@/components/UI/Skeleton';

import { CrytpoIcon } from './CryptoIcon';
import { PriceInfo } from './PriceInfo';
import StripeButton from './StripeButton';
import type { PriceCalculator } from './calculatePrice';
import calculatePrice from './calculatePrice';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import MailingListForm from '@/pages/(marketing)/mailing-list/components/MailingListForm';
import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import useRequest from '@/renderer/utils/useRequest';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { getProducts } from '../+Page';
import {
  OFFER_MATRIX_KEYS,
  OFFER_MATRIX_MAP,
  OFFER_MATRIX_TITLES,
  getFrequencyLabels,
  matrixFromOffers,
} from './productHelpers';

type MPO = API.MembershipProductOffer;

const BASE_STATS: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  noAction: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
  buyable: 0,
  charged: 0,
  roi: 0,
  total: 0,
};

const BASE_OFFER: MPO = {
  id: 0,
  ids: [0],
  plays: 0,
  price: 0,
};

const disclaimer = {
  href: '#',
  summary:
    'It should be expected that lines will move within seconds of plays being released. Despite our long-term history of winning (55-56% across thousands of plays), we are not immune to variance, poor streaks, or losing seasons. Please manage your bankroll and expectations accordingly. Releases may be viewed as "sharp" action that could lead to reduced limits or account closure at certain sportsbooks. Proper bankroll size, available outs, time and skills are needed to be successful and justify the cost of this service.',
};

function toFixed(num?: number) {
  if (typeof num !== 'number') {
    return num;
  }

  return (num || 0).toFixed(2);
}

const ProductPage = ({
  product,
  IconComponent,
  isLoading,
}: {
  product: API.MembershipProduct<true>;
  IconComponent?: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>>;
  isLoading: boolean;
}) => {
  const pageContext = usePageContext();
  const { locale, formatNumber } = useLocale();

  const balance = useMe()?.balance || 0;
  const discount = useMe()?.discount || 0;

  const offers = product?.offers;
  const addOnProductId = useMemo(() => {
    return offers?.find((offer) => offer.addOn?.productId)?.addOn?.productId || 0;
  }, [offers]);

  const addOnProductRequest = useRequest(getProducts, {
    manual: !addOnProductId,
    query: { data: { id: addOnProductId } },
    skipRefresh: true,
  });

  const addOnProduct = addOnProductRequest.data?.[0];

  const urlSelectedIndex = useMemo(() => {
    const key = pageContext?.urlParsed?.search?.key;

    if (key) {
      const foundIdx = product?.offers.findIndex((productOffer) => productOffer.key === key);

      if (foundIdx !== undefined) {
        return foundIdx;
      }
    }

    // Look for an offer marked as default
    const defaultIdx = product?.offers.findIndex((offer) => offer?.ui?.isDefault);
    if (defaultIdx !== undefined && defaultIdx !== -1) {
      return defaultIdx;
    }

    return 0;
  }, [product?.offers, pageContext?.urlParsed?.search?.key]);

  const [selectedIndex, setSelectedIndex] = useState<number>(urlSelectedIndex);

  const variety = offers?.[selectedIndex];

  const isSubscription = variety?.time?.subscription?.interval;
  const isAnnualSubscription = variety?.time?.subscription?.interval === '1 years';

  const stats = useMemo(() => {
    if (product?.stats) {
      return {
        ...product.stats,
        played:
          product.stats.played ||
          (+product.stats.loss || 0) +
            (+product.stats.pending || 0) +
            (+product.stats.push || 0) +
            (+product.stats.win || 0) -
            (product.stats.uncharged || 0),
      };
    }

    return undefined;
  }, [product?.stats]);

  const calcPrice = useMemo(() => {
    return calculatePrice({
      discount,
      stats: stats || BASE_STATS,
      variety: variety || BASE_OFFER,
    });
  }, [discount, stats, variety]);

  const getBestOfferMatch = useMemo(() => {
    return (
      key: keyof MPO,
      map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>,
      next: string,
      proposedOffer?: MPO,
    ) => {
      const matrix = map.get(next);
      const nextAllowedIndexes = Array.from(matrix?.indexes || []);

      if (nextAllowedIndexes.length === 0) {
        return 0;
      }

      if (nextAllowedIndexes.length === 1) {
        return nextAllowedIndexes[0];
      }

      let bestMatch = 0;
      let bestMatchCount = -1;

      if (offers) {
        for (const offerIdx in offers) {
          const offer = offers[offerIdx];
          const value = get(offer, key);

          if (
            value === matrix?.option ||
            (typeof value === 'object' && deepEqual(value, matrix?.option))
          ) {
            let matchCount = 0;

            if (!proposedOffer) {
              return +offerIdx;
            }

            for (const matrixKey of OFFER_MATRIX_KEYS.values()) {
              const offerValue = get(offer, matrixKey);
              const proposedValue = get(proposedOffer, matrixKey);

              if (
                offerValue === proposedValue ||
                (typeof offerValue === 'object' && deepEqual(offerValue, proposedValue))
              ) {
                matchCount++;
              }
            }

            if (matchCount > bestMatchCount) {
              bestMatchCount = matchCount;
              bestMatch = +offerIdx;
            }
          }
        }
      }

      return bestMatch;
    };
  }, [offers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(product): We want this to run when product changes
  useUpdateEffect(() => {
    setSelectedIndex(0);
  }, [product]);

  const triggerSetSelections = useMemo(() => {
    return (key: keyof MPO, map: Map<string, { option: MPO[keyof MPO]; indexes: Set<number> }>) =>
      (next: string) => {
        mixpanelTrack('order/client:selection', {
          product_id: product?.id,
          selection_key: key,
          selection_next: next,
        });
        setSelectedIndex(getBestOfferMatch(key, map, next, variety));
      };
  }, [variety, getBestOfferMatch, product?.id]);

  const matrices = useMemo(() => {
    return matrixFromOffers(product);
  }, [product]);

  const hasBettingTalkExtra = useMemo(() => {
    const apid = variety?.addOn?.productId;

    if (apid) {
      return product?.products?.[apid]?.tags?.includes('betting-talk') || apid === 49;
    }

    if (variety?.combos) {
      for (const { productId: pid } of variety.combos) {
        if (pid) {
          return product?.products?.[pid]?.tags?.includes('betting-talk') || pid === 49;
        }
      }
    }
  }, [variety, product?.products]);

  /** Something of a sprintf-type function for templating strings */
  const nestedReplace = useMemo(() => {
    return function nestedReplaceInner<
      O,
      M extends Map<string, unknown>,
      P extends PriceCalculator,
    >(str: string, option?: O, matrix?: M, calc?: P) {
      if (!str) {
        return str;
      }
      let cur = str;
      let next = str;

      do {
        cur = next;
        next = next.replace(/{([^{}]+)}/g, (match, inner) => {
          // Handle fallbacks with ||
          const parts = inner.split('||').map((p) => p.trim().replace(/^["']|["']$/g, ''));

          for (const part of parts) {
            // Evaluate this path in various contexts
            const value = (() => {
              if (part === '') {
                return '';
              }

              // Literal fallback text
              if (!/[.[\]]/.test(part) && !/^[a-zA-Z_]/.test(part)) {
                return part;
              }

              const [root, ...rest] = part.split('.');
              const path = rest.join('.');

              const someCalc = calc || calcPrice;

              if (root === 'option' && option) {
                return get(option, path, undefined);
              }
              if (root === 'variety' && variety) {
                return get(variety, path, undefined);
              }
              if (root === 'product' && product) {
                return get(product, path, undefined);
              }
              if (root === 'calc' && someCalc) {
                if (path === 'plays' && someCalc.plays > 300) {
                  return '';
                }
                return get(someCalc, path, undefined);
              }
              if (root === 'matrix' && matrix) {
                return get(Array.from(matrix.values()), path, undefined);
              }
              if (root === 'this' && option) {
                return path ? get(option, path, undefined) : option;
              }

              // Try plain key lookup in option as last resort
              return option ? get(option, part, undefined) : undefined;
            })();

            if (value !== undefined && value !== null && value !== '') {
              return value;
            }
          }

          return ''; // fallback if all fail
        });
      } while (next !== cur);

      return next;
    };
  }, [calcPrice, product, variety]);

  return (
    <div className="grid grid-flow-dense lg:grid-cols-7 lg:gap-x-8 xl:gap-x-16">
      <div className="mx-auto w-full max-w-2xl lg:col-span-4 lg:mt-0 lg:max-w-none">
        {/* Product details */}
        <div className="rounded-sm p-4 shadow-sm ring-1 ring-emerald-900/10 sm:p-10 dark:bg-white/5 dark:ring-white/10">
          <div className="mx-auto mt-4 max-w-2xl lg:col-span-3 lg:mt-0 lg:max-w-none">
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                {IconComponent && (
                  <IconComponent className="mr-4 hidden size-10 shrink-0 fill-zinc-800 stroke-2 sm:inline-block dark:fill-white" />
                )}
                <h1 className="font-bold text-2xl text-zinc-900 tracking-tight sm:text-3xl dark:text-zinc-100">
                  <Skeleton loading={isLoading}>{product?.title || 'Order'}</Skeleton>
                </h1>
              </div>

              {product?.ui?.href && (
                <a
                  href={product?.ui?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex w-fit items-center space-x-1 rounded-full bg-zinc-900/10 px-4 py-2 text-xs text-zinc-700 hover:bg-zinc-900/15 hover:text-zinc-900 dark:bg-white/20 dark:text-white"
                >
                  <span>{product?.ui?.href}</span>
                  <ArrowTopRightOnSquareIcon
                    height={16}
                    className="size-4 shrink-0"
                    aria-hidden="true"
                  />
                </a>
              )}

              <section aria-labelledby="information-heading" className="mt-3 sm:mt-4">
                <h2 id="information-heading" className="sr-only">
                  Product information
                </h2>

                <div className="mb-6 font-normal text-sm text-zinc-500 tracking-tight sm:mb-10 sm:font-medium xl:text-base dark:text-zinc-100">
                  {/* <h3>
                    <Skeleton loading={isLoading}>
                      {nestedReplace(
                        product?.includesDescription ||
                          product?.description ||
                          PRODUCT_DEFAULT_DESCRIPTION[
                            variety?.addOn
                              ? variety?.combos?.length
                                ? 'addOnCombos'
                                : 'addOn'
                              : variety?.combos?.length
                                ? 'combos'
                                : 'default'
                          ],
                      )}
                    </Skeleton>
                  </h3> */}
                  {product?.ui?.releaseTimes?.duration && (
                    <div className="mt-8 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                      <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                      <div className="font-bold text-sm text-zinc-700 dark:text-white">
                        {product?.ui?.releaseTimes?.duration}
                      </div>
                    </div>
                  )}
                  {product?.ui?.includes && (
                    <div className="mt-4 mb-10 text-zinc-500 dark:text-zinc-400">
                      <ul
                        role="list"
                        className="mt-4 flex flex-col gap-y-4 text-sm text-zinc-500 dark:text-zinc-400"
                      >
                        {product.ui.includes.map((highlight) => (
                          <li key={highlight} className="flex items-center gap-x-2">
                            <CheckIcon
                              className="size-6 flex-shrink-0 self-start text-emerald-500"
                              aria-hidden="true"
                            />
                            {typeof highlight === 'string' ? (
                              <span className="leading-6">
                                {nestedReplace(highlight)} {/* This will replace {calc.plays} */}
                              </span>
                            ) : (
                              <span className="leading-6">
                                {nestedReplace(highlight.text)}
                                <a
                                  href={highlight.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-zinc-900 underline hover:text-zinc-700 dark:text-white dark:hover:text-zinc-200"
                                >
                                  {highlight.linkText}
                                </a>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {product?.ui?.releaseTimes?.firstRelease && (
                    <div className="mt-4 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                      <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                      <div>
                        <div className="font-medium text-sm text-zinc-700 dark:text-zinc-200">
                          {product?.ui?.releaseTimes?.firstReleaseText || 'First possible release'}
                        </div>
                        <div className="font-bold text-base text-black dark:text-white">
                          {product?.ui?.releaseTimes?.firstRelease}
                        </div>
                      </div>
                    </div>
                  )}
                  {product?.ui?.currentResults && (
                    <div className="mt-10">
                      <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                        <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                          {product?.ui?.currentResults.map(
                            ({ label, value, percentage, units }) => (
                              <div key={label} className="flex flex-col text-left">
                                <div className="flex flex-row items-start space-x-4">
                                  <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                                  <div>
                                    <dt className="mt-0 font-bold text-sm text-zinc-700 dark:text-white">
                                      <span>{label}</span>
                                    </dt>
                                    <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                                      {value}{' '}
                                      <span className="font-normal text-sm text-zinc-400">
                                        ({percentage}%)
                                      </span>
                                      <span className="ms-2 w-fit whitespace-nowrap rounded bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-900 text-sm dark:bg-emerald-400/20 dark:text-emerald-50">
                                        {units} units
                                      </span>
                                    </dd>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </dl>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {product?.ui?.phases && (
              <div className="mb-8 space-y-3 rounded-lg border p-4">
                <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                  Phase Schedule
                </h3>
                <ul className="space-y-4">
                  {product?.ui?.phases.map((phase) => {
                    // const isCurrent = phase === currentPhase;
                    return (
                      <div
                        className={`flex w-full flex-row items-start space-x-2 rounded`}
                        key={phase.title}
                      >
                        <CalendarDaysIcon className="inline-block size-4" />
                        <li
                          key={phase.title}
                          className="w-full border-zinc-950/10 border-b border-dotted pb-1 dark:border-zinc-white/10"
                        >
                          <div className="flex flex-row justify-between">
                            <div>
                              <div className="font-semibold text-xs">
                                <span className="text-sm">{phase.title}</span>
                                <span className="ml-2 font-normal text-zinc-500">
                                  {(() => {
                                    return new Date(phase.start).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      timeZone: 'UTC',
                                    });
                                  })()} – {(() => {
                                    return new Date(phase.end).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      timeZone: 'UTC',
                                    });
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-zinc-700 dark:text-zinc-400">
                                Estimated {phase.volume} picks
                              </div>
                            </div>
                            <span className="font-semibold text-base text-zinc-900 tracking-tight dark:text-zinc-100">
                              ${phase.price.toLocaleString()}
                            </span>
                          </div>
                          {/* {isCurrent && proratedPrice !== null && (
                              <span className="ml-2 text-green-700">
                                (Join now for ${proratedPrice.toLocaleString()} prorated)
                              </span>
                            )} */}
                        </li>
                      </div>
                    );
                  })}
                </ul>
              </div>
            )}

            {!!matrices &&
              Array.from(matrices)
                .sort(([keyA], [keyB]) => {
                  const keyOrder = Array.from(OFFER_MATRIX_KEYS);
                  const indexA = keyOrder.indexOf(keyA);
                  const indexB = keyOrder.indexOf(keyB);

                  // If key not found in order, put it at the end
                  const finalIndexA = indexA === -1 ? 999 : indexA;
                  const finalIndexB = indexB === -1 ? 999 : indexB;

                  return finalIndexA - finalIndexB;
                })
                .map(([key, matrix]) => {
                  let lastPrice: string | number = Infinity;
                  let curPriceIndex = 0;

                  if (key === 'time.subscription.interval') {
                    return (
                      <RadioGroup
                        value={`${JSON.stringify(get(variety, key))}`}
                        onChange={triggerSetSelections(
                          key as keyof typeof OFFER_MATRIX_MAP,
                          matrix,
                        )}
                        aria-label="Subscription frequency"
                        className="mb-8 grid w-fit grid-cols-2 gap-x-1 rounded-full p-1 text-center font-semibold text-xs leading-5 ring-1 ring-zinc-200 ring-inset dark:ring-zinc-400"
                      >
                        {Array.from(matrix).map(([optionKey, map]) => {
                          const { option } = map;
                          const { label } = getFrequencyLabels(option as string);
                          return (
                            <Field
                              key={optionKey}
                              className="flex min-w-32 items-center gap-2 xl:min-w-40"
                            >
                              <Radio as={Fragment} value={optionKey}>
                                {({ checked }) => (
                                  <span
                                    className={clsx(
                                      'w-full cursor-pointer rounded-full px-2.5 py-1',
                                      checked
                                        ? 'bg-emerald-400/20 text-emerald-800 dark:bg-zinc-200 dark:text-black'
                                        : 'text-zinc-500 dark:text-zinc-200',
                                    )}
                                  >
                                    {label}
                                  </span>
                                )}
                              </Radio>
                            </Field>
                          );
                        })}
                      </RadioGroup>
                    );
                  }

                  if (key === 'addOn' && variety?.ui?.hideAddOns === true) {
                    const hasComboProduct62 = Array.isArray(variety?.combos)
                      ? variety.combos.some((combo) => combo.productId === 62)
                      : false;

                    // Return the message if hasComboProduct62, otherwise return null
                    return hasComboProduct62 ? (
                      <div className="my-8 flex size-full cursor-pointer flex-col items-start justify-between space-y-2 rounded-sm bg-white p-3 font-semibold text-sm text-zinc-900 shadow-sm ring-1 ring-zinc-500 sm:flex-1 dark:bg-white/10 dark:text-white">
                        <div className="w-full">
                          <div className="flex w-full flex-row items-center justify-between">
                            <span>Season Pass (4 Phases + Extras)</span>
                          </div>
                          <div className="mt-2 font-normal text-xs text-zinc-700 dark:text-zinc-200">
                            All 2025-26 CBB picks released by RAS. 4 phases + Season long extra
                            service.
                          </div>
                        </div>
                        <div className="mt-1 font-normal text-xs text-zinc-500 dark:text-zinc-300">
                          Estimated volume 320-410 picks
                        </div>
                      </div>
                    ) : null;
                  }

                  return (
                    <>
                      <RadioGroup
                        key={key}
                        value={`${JSON.stringify(get(variety, key))}`}
                        onChange={triggerSetSelections(
                          key as keyof typeof OFFER_MATRIX_MAP,
                          matrix,
                        )}
                        aria-label={key}
                        className="group mb-6 sm:mb-10"
                      >
                        <Label className="w-full truncate font-medium text-xs text-zinc-800 dark:text-zinc-200">
                          {product?.ui?.text?.[key] ||
                            OFFER_MATRIX_TITLES[key as keyof typeof OFFER_MATRIX_TITLES] ||
                            key}
                        </Label>

                        {!!(
                          product?.ui?.text?.[`${key}Subtitle`] ||
                          OFFER_MATRIX_TITLES[`${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES]
                        ) && (
                          <p className="py-2 text-xs text-zinc-500 italic dark:text-zinc-300">
                            {product?.ui?.text?.[`${key}Subtitle`] ||
                              OFFER_MATRIX_TITLES[
                                `${key}Subtitle` as keyof typeof OFFER_MATRIX_TITLES
                              ]}
                          </p>
                        )}
                        <div
                          className={clsx(
                            'mt-2 mb-4 grid grid-cols-2 justify-items-stretch gap-3',
                            matrix.size === 3 && 'md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3',
                          )}
                        >
                          {Array.from(matrix).map(([optionKey, map]) => {
                            const { option, indexes } = map;
                            const kt = key as keyof typeof OFFER_MATRIX_MAP;
                            const offerMatrixMap = OFFER_MATRIX_MAP[kt];
                            const offerIndex = indexes.has(selectedIndex)
                              ? selectedIndex
                              : getBestOfferMatch(kt, matrix, optionKey, variety);

                            const offer = offers?.[offerIndex];
                            const over = option as API.MembershipProductOfferAddOn | undefined;

                            const thisOffer = offer || BASE_OFFER;

                            const isAddOn = kt === 'addOn' || kt === 'combos';
                            const remoteProductId = isAddOn ? over && over.productId : false;
                            const remoteOfferId = (over && over.offerId) || 0;
                            const remoteProduct =
                              (!!remoteProductId && product?.products?.[remoteProductId]) ||
                              undefined;
                            const remoteOffer =
                              (remoteProduct && remoteProduct?.offers?.[remoteOfferId]) ||
                              undefined;

                            const offerCalc = calculatePrice({
                              discount,
                              stats: stats || BASE_STATS,
                              variety: thisOffer || BASE_OFFER,
                            });

                            const remoteCalc = calculatePrice({
                              discount,
                              stats: remoteProduct?.stats || BASE_STATS,
                              variety: remoteOffer || BASE_OFFER,
                            });

                            const visiblePrice = isAddOn
                              ? offerCalc.addOnPrice && offerCalc?.addOnPrice > 0
                                ? `${isAddOn ? '+' : ''}${formatNumber(offerCalc?.addOnPrice || 0, {
                                    maximumFractionDigits: 0,
                                  })}`
                                : '-'
                              : `${isAddOn ? '+' : ''}${formatNumber(offerCalc.basePrice, {
                                  maximumFractionDigits: 0,
                                })}`;

                            let lastPriceIndex = curPriceIndex;

                            if (lastPrice !== visiblePrice) {
                              if (lastPrice !== Infinity) {
                                curPriceIndex++;
                                lastPriceIndex++;
                              }
                              lastPrice = visiblePrice;
                            }

                            return (
                              <Field key={optionKey}>
                                <Radio as={Fragment} value={optionKey}>
                                  {({ checked }) => {
                                    // const priceDiff = offerCalc.price - (calcPrice.price || 0);

                                    return (
                                      <span
                                        className={clsx(
                                          // active
                                          //   ? 'ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-zinc-800'
                                          //   : '',
                                          checked
                                            ? 'bg-white shadow-sm ring-zinc-500 dark:bg-white/10 dark:text-white'
                                            : 'bg-zinc-900/5 ring-zinc-300 ring-inset hover:bg-zinc-900/2.5 dark:bg-white/2.5 dark:text-zinc-200 dark:ring-white/20 dark:hover:bg-white/5',
                                          'flex size-full cursor-pointer flex-col items-start justify-between space-y-2 rounded-sm p-3 font-semibold text-sm text-zinc-900 ring-1 sm:flex-1',
                                        )}
                                      >
                                        <div className="w-full">
                                          <div className="flex w-full flex-row items-center justify-between">
                                            <span>
                                              {(() => {
                                                const templateString =
                                                  offer?.ui?.option?.[key]?.title ||
                                                  (over
                                                    ? over.ui?.title ||
                                                      offer?.ui?.title ||
                                                      offerMatrixMap?.[0]
                                                    : offerMatrixMap?.[1] || `No ${key}`);

                                                // Handle any template that contains {this} with plays fallback
                                                if (
                                                  templateString &&
                                                  templateString.includes('{this}') &&
                                                  templateString.includes('plays||"plays"')
                                                ) {
                                                  const calcPlays = offerCalc?.plays;

                                                  if (calcPlays && calcPlays > 300) {
                                                    const playCount = option; // Use the option value (60, 10) for high tracking numbers
                                                    return `${playCount} plays`;
                                                  } else {
                                                    // Show the prorated calc.plays for normal cases
                                                    return `${calcPlays || 0} plays`;
                                                  }
                                                }

                                                // For all other cases, use nestedReplace
                                                return nestedReplace(
                                                  templateString,
                                                  over,
                                                  matrix,
                                                  remoteCalc || offerCalc,
                                                );
                                              })()}
                                            </span>
                                            <CheckCircleIcon
                                              height={20}
                                              className={clsx(
                                                'ml-1 size-5 shrink-0 fill-emerald-700 dark:fill-emerald-500',
                                                !checked && 'invisible',
                                              )}
                                            />
                                          </div>
                                          <div className="mt-2 font-normal text-xs text-zinc-700 dark:text-zinc-200">
                                            {nestedReplace(
                                              offer?.ui?.option?.[key]?.description ||
                                                (over
                                                  ? over?.ui?.description || offer?.ui?.description
                                                  : offerMatrixMap?.[2] || `No ${key}`),
                                              over,
                                              matrix,
                                              remoteCalc || offerCalc,
                                            )}
                                          </div>
                                        </div>
                                        {key === 'plays' && offer?.ui?.estimatedVolume && (
                                          <div className="mt-1 font-normal text-xs text-zinc-500 dark:text-zinc-300">
                                            Estimated volume: {offer.ui.estimatedVolume}
                                          </div>
                                        )}
                                        <>
                                          {discount ? (
                                            <div className="font-normal text-base text-zinc-500 line-through">
                                              {isAddOn
                                                ? offerCalc?.originalAddOnPrice
                                                  ? offerCalc?.originalAddOnPrice > 0 &&
                                                    formatNumber(
                                                      offerCalc?.originalAddOnPrice || 0,
                                                      {
                                                        maximumFractionDigits: 0,
                                                      },
                                                    )
                                                  : ''
                                                : formatNumber(offerCalc.originalPrice, {
                                                    maximumFractionDigits: 0,
                                                  })}
                                            </div>
                                          ) : (
                                            ''
                                          )}
                                          {offer?.ui?.earlyBird && (
                                            <p className="font-semibold text-red-500 text-xs tracking-wide">
                                              Early bird combo
                                            </p>
                                          )}
                                          <div className="flex w-full flex-row items-baseline justify-between">
                                            <div
                                              className={clsx(
                                                'text-base',
                                                'hidden group-has-[[data-priceindex="1"]]:block',
                                                offer?.ui?.earlyBird && 'text-red-500',
                                              )}
                                              data-priceindex={lastPriceIndex}
                                            >
                                              {visiblePrice}
                                              {offer?.ui?.earlyBird && (
                                                <span className="text-red-500 text-xs">
                                                  {' '}
                                                  &ndash; Save{' '}
                                                  {new Intl.NumberFormat(locale, {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    maximumFractionDigits: 0,
                                                  }).format(offer?.ui?.earlyBird)}
                                                </span>
                                              )}
                                              {offer?.ui?.comboSavings && (
                                                <span className="text-red-500 text-xs">
                                                  {' '}
                                                  &ndash; Save{' '}
                                                  {new Intl.NumberFormat(locale, {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                    maximumFractionDigits: 0,
                                                  }).format(offer?.ui?.comboSavings)}
                                                </span>
                                              )}
                                            </div>
                                            {key === 'plays' && offer?.ui?.prorated && (
                                              <div className="text-right font-normal text-xs text-zinc-700 italic dark:text-zinc-200">
                                                Prorated on {offer?.ui?.prorated}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                        {offer?.ui?.option?.[key]?.schedule ? (
                                          <div className="flex flex-row items-start space-x-2">
                                            <CalendarIcon height={16} className="size-4 shrink-0" />
                                            <span className="font-normal text-xs">
                                              {nestedReplace(
                                                offer?.ui?.option?.[key]?.schedule ||
                                                  (over
                                                    ? over?.ui?.schedule || offer?.ui?.schedule
                                                    : offerMatrixMap?.[2] || `No ${key}`),
                                                over,
                                                matrix,
                                                remoteCalc || offerCalc,
                                              )}
                                            </span>
                                          </div>
                                        ) : (
                                          ''
                                        )}
                                      </span>
                                    );
                                  }}
                                </Radio>
                              </Field>
                            );
                          })}
                        </div>
                      </RadioGroup>
                      {key === 'addOn' && !!addOnProduct && (
                        <div className="-mt-4 mb-8 flex flex-row items-start font-semibold text-sm">
                          <span className="text-zinc-700 dark:text-zinc-200">
                            Looking for just{' '}
                            {nestedReplace(addOnProduct.title || variety.addOn?.ui?.title || '')}?{' '}
                          </span>
                          <a
                            href={`/order/${addOnProduct.id}`}
                            className="ms-2 inline-flex flex-row items-center text-emerald-500 hover:text-emerald-400"
                          >
                            Order it here
                            <ArrowRightIcon className="ms-1 inline-block size-4" />
                          </a>
                        </div>
                      )}
                    </>
                  );
                })}

            <div>
              <PriceInfo
                {...calcPrice}
                loading={isLoading || !variety}
                earlyBird={variety?.ui?.earlyBird}
                locale={locale}
                variety={variety}
                stats={stats}
                discount={discount}
                balance={balance}
                earlyBirdEndDate={variety?.ui?.earlyBirdEndDate}
              />

              {variety?.ui?.prorated ? (
                <p className="mb-4 text-xs text-zinc-700 dark:text-zinc-200">
                  Prorated on {variety?.ui?.prorated}
                </p>
              ) : (
                <></>
              )}

              <div className="grid grid-cols-1 gap-x-6 gap-y-4">
                <StripeButton
                  product={product}
                  index={selectedIndex}
                  offer={variety}
                  price={calcPrice.price}
                  size="large"
                  source={pageContext.urlPathname}
                />
                <span className="text-sm text-zinc-700 dark:text-zinc-200">
                  {isAnnualSubscription ? (
                    <>
                      Billed{' '}
                      {new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      }).format(calcPrice.price)}{' '}
                      annually*
                    </>
                  ) : (
                    <>&nbsp;</>
                  )}
                </span>

                {!isSubscription && (
                  <a
                    href="/support-chat"
                    className="flex flex-row items-center text-xs text-zinc-500 dark:text-zinc-300"
                  >
                    <CrytpoIcon height={20} className="me-1 size-5 shrink-0" />
                    <span>Contact us to pay with cryptocurrency</span>
                  </a>
                )}
              </div>
            </div>

            {/* {product?.ui?.resultSummaries && (
              <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
                <div className="prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
                  <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                    {product?.ui?.resultSummaries.map(({ label, value, percentage, units }) => (
                      <div key={label} className="flex flex-col text-left">
                        <dt className="font-medium text-xs text-zinc-700 dark:text-zinc-300">
                          {label}
                        </dt>
                        <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                          {value}{' '}
                          <span className="font-normal text-sm text-zinc-400">({percentage}%)</span>
                          <span className="ms-2 w-fit whitespace-nowrap rounded bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-900 text-sm dark:bg-emerald-400/20 dark:text-emerald-50">
                            {units} units
                          </span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="mt-6 text-xs text-zinc-500 sm:mt-10 dark:text-zinc-400">
                  Results third party verified by{' '}
                  <a
                    href="https://betstamp.app/u/RAS"
                    target="_blank"
                    className="text-zinc-600 underline dark:text-zinc-300"
                    rel="noreferrer"
                  >
                    betstamp.
                  </a>
                </div>
              </div>
            )} */}

            {product?.ui?.note ? (
              <div className="mt-4 mb-6 rounded-sm bg-amber-50 p-4 text-amber-700 sm:mt-10 dark:bg-amber-300/20 dark:text-amber-400">
                <div className="flex space-x-2">
                  <InformationCircleIcon height={20} className="size-5 shrink-0" />
                  <div className="font-normal text-sm">{product?.ui?.note}</div>
                </div>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
        {/* Mailing list sign up form */}
        <div className="order-last mt-10 rounded-sm bg-zinc-50 p-4 text-zinc-900 shadow-xs ring-1 ring-zinc-900/10 lg:col-span-4 dark:bg-zinc-950 dark:text-zinc-50 dark:ring-zinc-50/5">
          <div className="flex flex-row items-center space-x-2">
            <EnvelopeIcon className="size-5" />
            <h3 className="font-medium text-sm">Not ready to sign up?</h3>
          </div>
          <p className="mt-2 mb-4 text-xs">
            Join our mailing list for free picks and service announcements.
          </p>
          <MailingListForm guarded={false} autoFocus={false} />
        </div>
        {hasBettingTalkExtra && (
          <></>
          // <div className="order-last mt-10 rounded-sm bg-sky-50 p-4 text-sky-950 shadow-xs ring-1 ring-sky-900/10 lg:col-span-4 dark:bg-sky-950 dark:text-sky-50 dark:ring-sky-50/5">
          //   <span className="pb-2 font-medium text-sm">Added bonus</span>
          //   <div className="mt-2 flex flex-row items-center justify-start space-x-4">
          //     <BettingTalkIcon className="size-12 fill-sky-900 dark:fill-sky-200" />
          //     <div>
          //       <h4 className="font-semibold">Betting Talk membership</h4>
          //       {/* <p className="text-sm font-normal">(up to $295 value)</p> */}
          //     </div>
          //   </div>

          //   <ul className="mt-4 ml-4 list-disc space-y-1 text-xs">
          //     <li>Access to Betting Talk private community</li>
          //     <li>Access to exclusive NFL content and discussion from Adam Chernoff</li>
          //     <li>
          //       Access to exclusive CFB/NBA content and discussion from Eddie Walls, including his
          //       popular blog posts
          //     </li>
          //     <li>Access to the entire RAS team and other long time sports betting pros</li>
          //   </ul>
          // </div>
        )}
        {disclaimer.summary && (
          <div className="order-last mt-10 lg:col-span-4">
            <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">Disclaimer</h3>
            <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">{disclaimer.summary}</p>
          </div>
        )}
      </div>
      {/* Product details */}
      <div className="mx-auto mt-10 max-w-2xl border-zinc-900/10 lg:col-span-3 lg:row-span-3 lg:mt-0">
        {product?.ui?.releaseTimes && (
          <>
            {product?.ui?.releaseTimes?.firstRelease && (
              <div className="mt-4 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                <div>
                  <div className="text-xs text-zinc-700 dark:text-zinc-200">
                    {product?.ui?.releaseTimes?.firstReleaseText || 'First possible release'}
                  </div>
                  <div className="font-medium text-sm">
                    {product?.ui?.releaseTimes?.firstRelease}
                  </div>
                </div>
              </div>
            )}
            {product?.ui?.releaseTimes?.nextRelease && (
              <div className="mt-4 mb-6 flex flex-row items-start space-x-4 sm:mb-10">
                <CalendarDaysIcon className="inline-flex size-6 shrink-0" />
                <div>
                  <div className="text-xs text-zinc-700 dark:text-zinc-200">
                    Next possible release
                  </div>
                  <div className="font-medium text-sm">
                    {product?.ui?.releaseTimes?.nextRelease}
                  </div>
                </div>
              </div>
            )}
            {product?.ui?.releaseTimes?.times && (
              <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
                <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                  Release schedule
                </h3>
                {product?.ui?.releaseTimes?.times && (
                  <div className="prose-sm text-zinc-500 dark:text-zinc-400">
                    <dl className="grid w-auto grid-flow-row grid-cols-1 gap-0 space-y-4 lg:grid-cols-1 lg:gap-0 lg:space-y-4">
                      {product?.ui?.releaseTimes?.times.map(({ label, description }) => (
                        <div key={label} className="flex flex-col text-left">
                          <dt className="font-medium text-xs text-zinc-700 dark:text-zinc-300">
                            {label}
                          </dt>
                          <dd className="mt-2 p-0 font-semibold text-xl text-zinc-900 tracking-tight dark:text-white">
                            {description}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {product?.ui?.newThisYear && (
          <div className="mt-10">
            <h3 className="font-medium text-emerald-700 text-sm dark:text-zinc-100">
              New for {product.ui?.newThisYear?.year}
              <SparklesIcon height={16} className="ml-2 inline-block size-4 shrink-0" />
            </h3>
            <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
              <ul role="list">
                {product.ui?.newThisYear?.list.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {product?.ui?.whatToExpect && (
          <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
            <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
              Service details
            </h3>
            <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
              <ul role="list">
                {product.ui?.whatToExpect.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {product?.ui?.includesGroups && (
          <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
            <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
              Possible release types
            </h3>
            <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
              {product.ui?.includesGroups.map((group) => (
                <div key={group?.title}>
                  <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                    {group?.title}
                  </h3>
                  <ul role="list">
                    {group?.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {product?.ui?.highlights && (
          <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
            <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">Highlights</h3>
            <div className="prose prose-sm mt-4 text-zinc-500 dark:text-zinc-400">
              <ul role="list">
                {product.ui?.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {product?.ui?.rasPlusNote ? (
          <div className="mt-4 mb-6 rounded-sm bg-emerald-50 p-4 text-emerald-700 first:mt-0 sm:mt-10 dark:bg-emerald-300/20 dark:text-emerald-400">
            <div className="flex space-x-2">
              <RASPlusIcon className="size-6 shrink-0 fill-emerald-800 dark:fill-emerald-400" />
              <div className="font-normal text-sm">
                {product?.ui?.rasPlusNote}{' '}
                <a
                  href="/order/55"
                  className="group inline-flex items-center font-semibold text-emerald-700 dark:text-emerald-400"
                >
                  <span>Learn more</span>
                  <ChevronRightIcon height={16} className="size-4 shrink-0 group-hover:hidden" />
                  <ArrowRightIcon
                    height={16}
                    className="hidden size-4 shrink-0 group-hover:inline-block"
                  />
                </a>
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
        {product?.ui?.results && (
          <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
            <div className="mx-auto flex w-full flex-col sm:w-full lg:flex lg:flex-auto lg:justify-end">
              <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
                {variety?.sport === 'nfl' ? 'Past seasons & totals results' : 'Past results'}
              </h3>
              <span className="my-3 text-xs text-zinc-700 dark:text-zinc-200">
                Results third party verified by{' '}
                <a
                  href="https://betstamp.app/u/RAS"
                  target="_blank"
                  className="text-zinc-900 underline dark:text-white/70"
                  rel="noreferrer"
                >
                  betstamp
                </a>
                .
              </span>
              <Table>
                <TableHeader className="hidden md:contents">
                  <TableRow>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                      Season
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                      Record
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                      Units
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm sm:pr-3 sm:pl-0 dark:bg-transparent dark:text-white">
                      Win rate
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product?.ui?.results.map(({ label, value, percentage, units }) => (
                    <TableRow key={label} className="font-normal hover:group-[&]/tbody:bg-initial">
                      <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm first-letter:uppercase sm:pr-3 dark:text-zinc-200">
                        <span className="inline-block first-letter:uppercase">
                          {`${label || '-'}`.replace(/-+/g, ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200">
                        {value}
                      </TableCell>
                      <TableCell
                        className={clsx(
                          'w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200',
                          units >= 0 ? 'text-emerald-500' : 'text-red-400',
                        )}
                      >
                        {units >= 0 ? '+' : ''}
                        {toFixed(units)}
                      </TableCell>
                      <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3 dark:text-zinc-200">
                        {toFixed(percentage)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {product?.ui?.faqs && product.ui.faqs.length > 0 && (
          <div className="mt-10 border-zinc-900/10 border-t pt-10 first:mt-0 first:border-t-0 first:pt-0 dark:border-zinc-50/10">
            <h3 className="font-medium text-sm text-zinc-900 dark:text-zinc-100">
              Frequently asked questions
            </h3>
            {product?.ui?.faqs?.map((faq) => (
              <Fragment key={faq.question}>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  <dt className="mt-10 font-medium text-zinc-900 dark:text-zinc-100">
                    {faq.question}
                  </dt>
                  <dd className="prose prose-sm mt-2 max-w-none text-zinc-500 dark:text-zinc-400">
                    <p>{faq.answer}</p>
                  </dd>
                </div>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
